import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SpinnerService } from '../spinner/spinner.service';
import { Observable, of } from 'rxjs';
import { Credential, CredentialValidation } from '../../models/appcloud/credential.model';
import { HttpHeaderConstants } from '../http-header.constant';
import { catchError, finalize } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class CredentialService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private CREDENTIALS_URL: string = environment.publicApiHost + '/appcloud/credentials';

    get(id: number, additionalHeaders: any = {}): Observable<Credential> {
        this.#spinnerService.addProcess('CredentialService.get.' + id, 'Retrieving your credentials...');

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.get<Credential>(this.CREDENTIALS_URL + '/' + id, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve your credentials. Please try again later.'
                );
                return of<Credential>();
            }),
            finalize(() => this.#spinnerService.completeProcess('CredentialService.get.' + id))
        );
    }

    searchByAccountId(accountId: string, additionalHeaders: any = {}): Observable<Credential[]> {
        this.#spinnerService.addProcess(
            'CredentialService.searchByAccountId.' + accountId,
            'Locating your credential information...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('accountId', accountId),
        };
        return this.#http.get<Credential[]>(this.CREDENTIALS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve credential data. Please try again later.'
                );
                return of<Credential[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('CredentialService.searchByAccountId.' + accountId))
        );
    }

    create(newCredential: Credential, additionalHeaders: any = {}): Observable<Credential> {
        this.#spinnerService.addProcess(
            'CredentialService.create.' + newCredential.accountId,
            'Saving your credential information...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<Credential>(this.CREDENTIALS_URL, newCredential, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while saving credential data. Please try again later.'
                );
                return of<Credential>();
            }),
            finalize(() => this.#spinnerService.completeProcess('CredentialService.create.' + newCredential.accountId))
        );
    }

    update(id: number, credential: Credential, additionalHeaders: any = {}): Observable<Credential> {
        this.#spinnerService.addProcess('CredentialService.update.' + id, 'Updating your credential information...');

        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http.patch<Credential>(this.CREDENTIALS_URL + '/' + id, credential, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while updating your credential information. Please try again later.'
                );
                return of<Credential>();
            }),
            finalize(() => this.#spinnerService.completeProcess('CredentialService.update.' + id))
        );
    }

    validate(
        credentialValidation: CredentialValidation,
        credentialId?: number,
        additionalHeaders: any = {}
    ): Observable<CredentialValidation> {
        this.#spinnerService.addProcess('CredentialService.validate', 'Validating your credential information...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };
        return this.#http
            .put<CredentialValidation>(this.buildUrl(credentialId), credentialValidation, httpOptions)
            .pipe(
                catchError(error => {
                    const errorMsg = this.getErrorMsg(error);
                    this.#errorHandlingService.handleHttpError(error, false, errorMsg);
                    return of<CredentialValidation>();
                }),
                finalize(() => this.#spinnerService.completeProcess('CredentialService.validate'))
            );
    }

    private getErrorMsg(error: any): string {
        if (error.error.status === 401) {
            return error.error.message;
        } else {
            return 'An error occurred while validating your credential information. Please try again later.';
        }
    }

    private buildUrl(credentialId?: number): string {
        if (credentialId) {
            return `${this.CREDENTIALS_URL}/${credentialId}/validations`;
        } else {
            return `${this.CREDENTIALS_URL}/validations`;
        }
    }
}
